import { apiFetch, downloadFile } from "../../lib/http";
import React from "react";
import { PageShell } from "../../components/layout/PageShell";
import { Skeleton } from "../../components/ui/Skeleton";
import { Chip } from "../../components/ui/Chip";
import { Download, Info } from "lucide-react";
import toast from "react-hot-toast";

type UploadedFile = {
  id: string;
  bank: string | null;
  original_name: string;
  storage_path: string;
  mime_type: string | null;
  size_bytes: number | null;
  uploaded_at: string;
  value_date_min: string | null;
  value_date_max: string | null;
};

const BANKS = [
  { id: "", label: "Tutte" },
  { id: "stonex", label: "StoneX" },
  { id: "icbc", label: "ICBC" },
  { id: "julius", label: "Julius Baer" },
];

function formatBytes(bytes?: number | null) {
  if (!bytes || bytes <= 0) return "—";
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
}

export default function UploadedFilesPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [rows, setRows] = React.useState<UploadedFile[]>([]);

  const [bank, setBank] = React.useState<string>("");
  const [uploadedFrom, setUploadedFrom] = React.useState<string>("");
  const [uploadedTo, setUploadedTo] = React.useState<string>("");
  const [valueFrom, setValueFrom] = React.useState<string>("");
  const [valueTo, setValueTo] = React.useState<string>("");

  const [limit, setLimit] = React.useState<number>(100);
  const [offset, setOffset] = React.useState<number>(0);

  const reload = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: string[] = [];
      if (bank) params.push(`bank=${encodeURIComponent(bank)}`);
      if (uploadedFrom) params.push(`uploaded_from=${encodeURIComponent(uploadedFrom)}`);
      if (uploadedTo) params.push(`uploaded_to=${encodeURIComponent(uploadedTo)}`);
      if (valueFrom) params.push(`value_from=${encodeURIComponent(valueFrom)}`);
      if (valueTo) params.push(`value_to=${encodeURIComponent(valueTo)}`);
      params.push(`limit=${limit}`);
      params.push(`offset=${offset}`);
      const qs = params.length ? `?${params.join("&")}` : "";
      const res = await apiFetch(`/api/files${qs}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setRows(json.rows || []);
    } catch (e: any) {
      setError(e?.message || "Errore");
    } finally {
      setLoading(false);
    }
  }, [bank, uploadedFrom, uploadedTo, valueFrom, valueTo, limit, offset]);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const clearFilters = () => {
    setBank("");
    setUploadedFrom("");
    setUploadedTo("");
    setValueFrom("");
    setValueTo("");
    setOffset(0);
  };

  return (
    <PageShell title="File caricati" subtitle="Elenco e filtri per banca e date">
      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Banca</label>
            <select
              className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm bg-white"
              value={bank}
              onChange={(e) => { setBank(e.target.value); setOffset(0); }}
            >
              {BANKS.map((b) => (
                <option key={b.id} value={b.id}>{b.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Caricati dal</label>
            <input type="date" className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
              value={uploadedFrom} onChange={(e) => { setUploadedFrom(e.target.value); setOffset(0); }} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Caricati al</label>
            <input type="date" className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
              value={uploadedTo} onChange={(e) => { setUploadedTo(e.target.value); setOffset(0); }} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Value date da</label>
            <input type="date" className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
              value={valueFrom} onChange={(e) => { setValueFrom(e.target.value); setOffset(0); }} />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Value date a</label>
            <input type="date" className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm"
              value={valueTo} onChange={(e) => { setValueTo(e.target.value); setOffset(0); }} />
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <div className="flex gap-2">
            {bank && <Chip onRemove={() => setBank("")}>Banca: {bank}</Chip>}
            {uploadedFrom && <Chip onRemove={() => setUploadedFrom("")}>Caricati dal {new Date(uploadedFrom).toLocaleDateString("it-IT")}</Chip>}
            {uploadedTo && <Chip onRemove={() => setUploadedTo("")}>Caricati al {new Date(uploadedTo).toLocaleDateString("it-IT")}</Chip>}
            {valueFrom && <Chip onRemove={() => setValueFrom("")}>Value date da {new Date(valueFrom).toLocaleDateString("it-IT")}</Chip>}
            {valueTo && <Chip onRemove={() => setValueTo("")}>Value date a {new Date(valueTo).toLocaleDateString("it-IT")}</Chip>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs hover:bg-gray-50"
              onClick={clearFilters}
            >
              Pulisci filtri
            </button>
            <select
              className="rounded-md border border-gray-300 px-2 py-1 text-xs bg-white"
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-4 space-y-3">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-40 w-full" />
        </div>
      )}

      {!loading && error && (
        <div className="mt-4 text-sm text-red-600">{error}</div>
      )}

      {!loading && !error && (
        <div className="mt-4 rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Nome file</th>
                  <th className="px-3 py-2 text-left font-semibold">Banca</th>
                  <th className="px-3 py-2 text-left font-semibold">Caricato il</th>
                  <th className="px-3 py-2 text-left font-semibold">Value date min</th>
                  <th className="px-3 py-2 text-left font-semibold">Value date max</th>
                  <th className="px-3 py-2 text-right font-semibold">Dimensione</th>
                  <th className="px-3 py-2 text-center font-semibold">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">{r.original_name}</td>
                    <td className="px-3 py-2">{r.bank ? r.bank.toUpperCase() : "—"}</td>
                    <td className="px-3 py-2">
                      {r.uploaded_at ? new Date(r.uploaded_at).toLocaleString("it-IT") : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {r.value_date_min ? new Date(r.value_date_min).toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td className="px-3 py-2">
                      {r.value_date_max ? new Date(r.value_date_max).toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatBytes(r.size_bytes)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            downloadFile(`/api/files/${r.id}/download`, r.original_name).catch(() =>
                              toast.error("Download non riuscito")
                            )
                          }
                          className="text-blue-600 hover:text-blue-800"
                          title="Scarica file"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <span
                          className="text-gray-400 hover:text-gray-600 cursor-pointer relative group"
                          title={r.id}
                        >
                          <Info className="h-4 w-4" />
                          <span className="absolute right-0 top-6 z-10 hidden group-hover:block bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap font-mono shadow-lg">
                            {r.id}
                          </span>
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td className="px-3 py-6 text-center text-sm text-gray-500" colSpan={7}>
                      Nessun file trovato con i filtri correnti
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Paginazione semplice */}
          <div className="flex items-center justify-between p-2 text-xs text-gray-600">
            <div>
              Mostra {rows.length} elementi
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="px-2 py-1 rounded-md border border-gray-300 bg-white disabled:opacity-50"
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
                disabled={offset <= 0}
              >
                ‹ Precedenti
              </button>
              <button
                type="button"
                className="px-2 py-1 rounded-md border border-gray-300 bg-white"
                onClick={() => setOffset((o) => o + limit)}
              >
                Successivi ›
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}


